/**
 * Practice (04 §1, 08 §1): pick any of the thirty-six missions, its terrain, light, weather, threat and seed,
 * read the briefing, fly it. Nothing is at stake - no ranger, no merit, no record - which is the point: it is
 * where a mission's special rule can be learnt without paying for the lesson. The missions are a grouped list
 * (practiceMissions.ts): the 1987 twelve first, then the expansion by category.
 */

import './rosters.css';
import './practice.css';
import { DOG_TEAMS } from '../../meta/content/dogs';
import { dogTeamsFor, STANDARD } from '../../meta/dogs';
import type { ArchetypeId, Biome, MissionParams, TimeOfDay, WeatherKind } from '../../sim/types';
import type { ArchetypeInfo } from './archetypes';
import { ARCHETYPES, archetypeInfo, archetypeNumber, BIOMES, freshSeed, TIMES_OF_DAY, weathersFor } from './archetypes';
import type { Ctx, Screen } from './context';
import { box, button, el, textField } from './dom';
import { factChips } from './missionTags';
import { missionList } from './practiceMissions';
import type { Choice } from './widgets/choiceRow';
import { choiceRow } from './widgets/choiceRow';
import { frame } from './widgets/frame';
import type { KeyHint } from './widgets/keyHints';
import { NAV_HINTS } from './widgets/keyHints';

const SEED_DIGITS = 10;
const SEED_MAX = 0x7fffffff;
const HINTS: readonly KeyHint[] = [...NAV_HINTS, { keys: 'PGUP/PGDN', label: 'NEXT GROUP OF MISSIONS' }];

/** Digits only, at most ten, and a seed in 1..2^31-1 (an empty field is seed 1). Pure. */
export function sanitiseSeed(text: string): { text: string; seed: number } {
  const digits = text.replace(/\D+/g, '').slice(0, SEED_DIGITS);
  const seed = Math.min(SEED_MAX, Math.max(1, digits === '' ? 1 : Number(digits)));
  return { text: digits, seed };
}

/** What the ABOUT panel says of a mission: name, category and tags, the one-line summary, where it is at home. */
function aboutMission(ctx: Ctx, info: ArchetypeInfo): HTMLElement[] {
  const d = ctx.describe(info.id);
  const chips = el('div', 'menu-row menu-row-wrap');
  const category = ctx.describe(`category:${info.category}`);
  const chip = el('span', 'menu-chip menu-chip-dim', category.name);
  chip.title = category.text;
  chips.append(chip);
  chips.append(...factChips(ctx.describe, info.id));
  return [
    el('div', 'menu-practice-about-name menu-paper', `${archetypeNumber(info.id)} ${d.name}`),
    chips,
    el('div', 'menu-prose menu-paper', d.text),
    el('div', 'menu-prose menu-dim menu-small',
      `NATIVE GROUND: ${ctx.describe(info.biome).name}. BASE THREAT ${info.baseThreat}.`),
  ];
}

export function practiceScreen(ctx: Ctx): Screen {
  const f = frame('practice', 'PRACTICE', HINTS);
  f.info.append(el('span', '', 'NOTHING AT STAKE: NO RANGER, NO MERIT, NO RECORD'));

  const named = <T extends string>(ids: readonly T[]): Array<Choice<T>> =>
    ids.map((id) => {
      const d = ctx.describe(id);
      return { value: id, label: d.name, title: d.text };
    });

  const start = ARCHETYPES[0];
  let archetype: ArchetypeId = start?.id ?? 'munitions-depot';
  let biome: Biome = archetypeInfo(archetype).biome;
  let timeOfDay: TimeOfDay = 'day';
  let weather: WeatherKind = 'clear';
  let threat = archetypeInfo(archetype).baseThreat;

  const aboutTitle = el('div', 'menu-panel-title', 'ABOUT THIS MISSION');
  const about = el('div', 'menu-col menu-grow menu-scroll');
  about.setAttribute('aria-live', 'polite');
  const preview = (info: ArchetypeInfo): void => {
    aboutTitle.textContent = info.id === archetype ? 'ABOUT THIS MISSION - CHOSEN' : 'ABOUT THIS MISSION';
    about.replaceChildren(...aboutMission(ctx, info));
  };

  const weatherRow = choiceRow<WeatherKind>('WEATHER', named(weathersFor(biome)), weather, (value) => {
    weather = value;
  });
  const setBiome = (next: Biome): void => {
    biome = next;
    // Weather belongs to the terrain: a sandstorm does not follow the player into the arctic.
    weatherRow.setChoices(named(weathersFor(biome)));
    weather = weatherRow.value();
  };
  const biomeRow = choiceRow<Biome>('TERRAIN', named(BIOMES), biome, setBiome);
  const timeRow = choiceRow<TimeOfDay>('LIGHT', named(TIMES_OF_DAY), timeOfDay, (value) => {
    timeOfDay = value;
  });
  // ROUND8: Practice flies dog teams by the rating, at Standard - say so beside the rating that brings them.
  const dogsLine = el('div', 'menu-prose menu-dim menu-small');
  const paintDogs = (): void => {
    const teams = dogTeamsFor(threat, STANDARD, []);
    dogsLine.textContent = teams === 0 ? `NO DOG TEAMS BELOW THREAT ${DOG_TEAMS.oneFrom}.`
      : `DOG TEAMS AT THIS THREAT: ${teams}. THEY FOLLOW YOUR TRAIL.`;
  };
  const threats = Array.from({ length: 9 }, (_, i) => ({ value: i + 1, label: String(i + 1) }));
  const threatRow = choiceRow<number>('THREAT 1-9', threats, threat, (value) => {
    threat = value;
    paintDogs();
  });
  paintDogs();

  const chosenLine = el('div', 'menu-practice-chosen menu-paper');
  const paintChosen = (): void => {
    chosenLine.textContent = `TO FLY: ${archetypeNumber(archetype)} ${ctx.describe(archetype).name}`;
  };

  const list = missionList({
    describe: ctx.describe,
    selected: archetype,
    onPreview: preview,
    onSelect: (info) => {
      // A mission brings its own ground and its own rating; both can then be changed.
      archetype = info.id;
      biomeRow.set(info.biome);
      setBiome(info.biome);
      threat = info.baseThreat;
      threatRow.set(threat);
      paintDogs();
      paintChosen();
      preview(info);
    },
  });

  // The field is the one copy of the seed: whatever it shows is what gets flown.
  const seedField = textField('SEED', String(freshSeed()), SEED_DIGITS);
  seedField.input.setAttribute('inputmode', 'numeric');
  seedField.input.addEventListener('input', () => {
    seedField.input.value = sanitiseSeed(seedField.input.value).text;
  });
  const random = button('RANDOM', () => (seedField.input.value = String(freshSeed())), 'menu-btn-small');
  seedField.row.append(random);

  // What he chose, and nothing derived: the briefing and the deployment stamp the dog teams (`practiceParams`).
  const params = (): MissionParams => ({
    archetype,
    biome,
    timeOfDay,
    weather,
    threat,
    seed: sanitiseSeed(seedField.input.value).seed,
    modifiers: [],
    intel: [],
  });
  const brief = button('BRIEFING', () =>
    ctx.nav.briefing({ mode: 'practice', params: params(), node: null, rangerId: null }),
  );
  const back = button('BACK', () => ctx.nav.title());
  paintChosen();
  preview(archetypeInfo(archetype));

  const form = box(
    'menu-practice-form menu-col', biomeRow.el, timeRow.el, weatherRow.el, threatRow.el, dogsLine, seedField.row,
  );
  const side = box(
    'menu-col menu-practice-side',
    box('menu-panel menu-col menu-practice-about', aboutTitle, about),
    form,
    el('div', 'menu-prose menu-dim menu-small', 'THE SAME SEED AND SETTINGS ALWAYS BUILD THE SAME GROUND.'),
    chosenLine,
    box('menu-row menu-practice-actions', back, brief),
  );
  const missions = box('menu-panel menu-col menu-grow', el('div', 'menu-panel-title', 'MISSION'), list.el);
  f.body.append(missions, side);
  return { el: f.el, back: () => ctx.nav.title(), initialFocus: () => brief };
}
